import DeviceTemplateApi from "../../api/DeviceTemplateApi";
import { ImportOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Popconfirm, Space, Table, message } from "antd";
import { ColumnType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import moment from "moment";

import StringUtils from "../../util/StringUtils";
import DeviceTemplateDetails from "./components/device-template-details/DeviceTemplateDetails";
import { DeviceTemplateManagementWrapper, TableWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./DeviceTemplateManagement.styled";
import { CreateDeviceTemplateWizard } from "./components/create-device-template-wizard/CreateDeviceTemplateWizard";
import DeviceTemplateUtils from "./util/DeviceTemplateUtils";
import PermissionWrapper from "../../components/permission/PermissionWrapper";
import FileUtils from "../../util/FileUtils";
import Base64Utils from "../../util/Base64Utils";

interface DeviceTemplateManagementProps {

}

const DeviceTemplateManagement: React.FC<DeviceTemplateManagementProps> = (props)=> {
    // api
    let deviceTemplateApi = new DeviceTemplateApi();
    const [messageApi, contextHolder] = message.useMessage();
    let navigate = useNavigate();

    // state
    let [loading, setLoading] = useState(true);
    let [deviceTemplateViews, setDeviceTemplateViews] = useState([]);
    let [searchValue, setSearchValue] = useState("");
    let [openCreateDeviceTemplateModal, setOpenCreateDeviceTemplateModal] = useState(false);
    let [deviceTemplateFromFile, setDeviceTemplateFromFile] = useState(null);
    let [openImportDeviceTemplateModal, setOpenImportDeviceTemplateModal] = useState(false);

    // events
    let onEditDeviceTemplateDetails = (deviceTemplateView: any)=> {
        navigate(`./device-template/${deviceTemplateView.deviceTemplate.id}`);
    };

    let onExportDeviceTemplate = (deviceTemplateView: any)=> {
        let frame:any=document.getElementById('download-frame');
        if(frame==null) {
            frame=document.createElement('iframe');
            frame.id='download-frame';
            frame.name='download-frame';
            frame.width='0';
            frame.height='0';
            document.body.appendChild(frame);
        }

        let deviceTemplate = JSON.parse(JSON.stringify(deviceTemplateView.deviceTemplate));
        deviceTemplate.id = undefined;
        deviceTemplate.createdTime = undefined;
        deviceTemplate.createdBy = undefined;
        deviceTemplate.updatedTime = undefined;
        deviceTemplate.updatedBy = undefined;
        deviceTemplate.deleted = undefined;
        deviceTemplate.tenantId = undefined;

        let blob=new Blob([JSON.stringify(deviceTemplate, null, 4)], {
            'type':'text/plain; charset=utf-8',
        });

        let objUrl=URL.createObjectURL(blob);
        let fileName=deviceTemplateView.deviceTemplate.name + '.device-template';

        let element=document.createElement('a');
        element.style.position = 'absolute';
        element.style.visibility = 'hidden';
        element.download = fileName;
        element.href = objUrl;
        element.target = 'download-frame';

        document.body.appendChild(element);
        element.click();
        element.remove();
        
        // release the data
        URL.revokeObjectURL(objUrl);
    };

    let onViewDevices = (deviceTemplateView: any)=> {
        navigate(`/device-management?deviceTemplateId=${deviceTemplateView.deviceTemplate.id}`)
    };

    let onAddDeviceTemplateClick = ()=> {
        setOpenCreateDeviceTemplateModal(true);
    };

    let onImportDeviceTemplateClick = ()=> {
        let fileSelect=document.createElement('input');
        fileSelect.type='file';
        fileSelect.style.display = 'none';
        fileSelect.addEventListener('click', (event)=>{
            event.stopPropagation();
        });

        let onSelectFile=()=>{
            let file: any=fileSelect.files[0];
            let reader=new FileReader();
            reader.onload=(e)=>{
                // complete callback
                let name=file.name;
                name=FileUtils.getFileNameFromPath(name);

                let target: any=e.target;
                let result:string=target.result;
                
                let startFlag='base64,';
                let base64Data=result.substring(result.indexOf(startFlag) + startFlag.length);
                let data=Base64Utils.atobUTF8(base64Data);
                let deviceTemplate=JSON.parse(data);
                deviceTemplate.id = null;
                let nameConflicts = deviceTemplateViews.find((deviceTemplateView)=>{
                    return deviceTemplateView.deviceTemplate.name === deviceTemplate.name;
                });
                if(nameConflicts) {
                    let newNameSuffix=(new Date().getTime() % 10000 + Math.round(Math.random() * 10000)).toString(16);
                    let newDeviceTemplateName=deviceTemplate.name + '-' + newNameSuffix;
                    deviceTemplate.name = newDeviceTemplateName;
                }

                // open create device template dialog
                setDeviceTemplateFromFile(deviceTemplate);
                setOpenImportDeviceTemplateModal(true);
            };
            
            reader.readAsDataURL(file);
        };
        fileSelect.addEventListener('change', (event)=>{
            event.stopPropagation();
            onSelectFile();
        });

        fileSelect.click();
    }

    let onDeleteDeviceTemplate = async (deviceTemplateView: any)=> {
        await deviceTemplateApi.delete(deviceTemplateView.deviceTemplate.id);
        messageApi.open({
            type: 'success',
            content: '删除设备模板成功',
        });
        loadDeviceTemplateViews();
    };

    let onInputChange = (e)=> {
        setSearchValue(e.target.value);
    };

    // load deviceTemplate views
    let loadDeviceTemplateViews = async ()=> {
        setLoading(true);
        let deviceTemplateViews = (await deviceTemplateApi.getAllDeviceTemplateViews()).data.data;
        setDeviceTemplateViews(deviceTemplateViews);
        setLoading(false);
    }

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '模板名称',
            dataIndex: 'deviceTemplate.name',
            key: 'deviceTemplate.name',
            align: 'center',
            render: (text, record, index)=> {
                return <a onClick={()=> onEditDeviceTemplateDetails(record)}>{record.deviceTemplate.name}</a>
            }
        },
        {
            title: '产品型号',
            dataIndex: 'product.modelName',
            key: 'product.modelName',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.product?.modelName || record.deviceTemplate.productModel)}</span>
            }
        },
        {
            title: '节点类型',
            dataIndex: 'deviceTemplate.nodeType',
            key: 'deviceTemplate.nodeType',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ DeviceTemplateUtils.nodeType2Text(record.deviceTemplate.nodeType) }</span>
            }
        },
        {
            title: '设备数量',
            dataIndex: 'statistics.deviceCount',
            key: 'statistics.deviceCount',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ record.statistics.deviceCount }</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'deviceTemplate.remarks',
            key: 'deviceTemplate.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.deviceTemplate.remarks)}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <PermissionWrapper permissionCodePath="device-management.device-template.update-device-template">
                        <a onClick={()=> onEditDeviceTemplateDetails(record)}>编辑模板</a>
                    </PermissionWrapper>

                    <a onClick={()=> onExportDeviceTemplate(record)}>导出模板</a>
                    <a onClick={()=> onViewDevices(record)}>管理设备</a>

                    <PermissionWrapper permissionCodePath="device-management.device-template.delete-device-template">
                        {record.statistics.deviceCount <= 0 && <Popconfirm
                                title="删除设备模板"
                                description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>确认要删除设备模板么?</div> }
                                onConfirm={()=> onDeleteDeviceTemplate(record)}
                                okText="确定"
                                cancelText="取消"
                            > 
                            <a>删除模板</a>
                        </Popconfirm> }
                    </PermissionWrapper>
                </Space>
            }
        }
    ];

    let filteredDeviceTemplateViews = [].concat(deviceTemplateViews);
    if (searchValue) {
        filteredDeviceTemplateViews = filteredDeviceTemplateViews.filter((e)=> {
            if (e.deviceTemplate.name.indexOf(searchValue)>=0 
            || e.deviceTemplate.productModel.indexOf(searchValue)>=0) {
                return true;
            }
            return false;
        });
    }

    // effect
    useEffect(()=> {
        loadDeviceTemplateViews();
    }, []);
    
    // render
    return (
        <DeviceTemplateManagementWrapper>
            {contextHolder}
            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <PermissionWrapper permissionCodePath="device-management.device-template.create-device-template">
                            <Button type="primary" icon={<PlusOutlined />} onClick={onAddDeviceTemplateClick}>创建模板</Button>
                        </PermissionWrapper>
                        <PermissionWrapper permissionCodePath="device-management.device-template.create-device-template">
                            <Button type="primary" icon={<ImportOutlined />} danger onClick={onImportDeviceTemplateClick}>导入模板</Button>
                        </PermissionWrapper>
                    </Space>
                </ToolBarLeftWrapper>

                <ToolBarRightWrapper>
                    <Input 
                        placeholder="请输入模板名称" 
                        suffix={<SearchOutlined />} 
                        size="middle" 
                        onChange={onInputChange} />
                </ToolBarRightWrapper>

                <Modal open={openCreateDeviceTemplateModal} 
                    onClose={()=> setOpenCreateDeviceTemplateModal(false)} 
                    destroyOnClose={true}
                    footer={null} 
                    closable={false}
                    width={1000}
                    height={600}
                    >
                    <CreateDeviceTemplateWizard 
                        onClose={()=> setOpenCreateDeviceTemplateModal(false)} 
                        onComplete={()=> loadDeviceTemplateViews()}
                    />
                </Modal>
                <Modal open={openImportDeviceTemplateModal} 
                    onClose={()=> setOpenImportDeviceTemplateModal(false)} 
                    destroyOnClose={true}
                    footer={null} 
                    closable={false}
                    width={1000}
                    height={600}
                    >
                    <CreateDeviceTemplateWizard 
                        onClose={()=> setOpenImportDeviceTemplateModal(false)} 
                        onComplete={()=> loadDeviceTemplateViews()}
                        deviceTemplate={deviceTemplateFromFile}
                    />
                </Modal>
            </ToolBarWrapper>
            <TableWrapper>
                <Table 
                    rowKey={(record)=> record.deviceTemplate.id}
                    columns={columns} 
                    dataSource={filteredDeviceTemplateViews}
                    loading={loading}
                />
            </TableWrapper>

            <Routes>
                <Route path="device-template/:deviceTemplateId" element={<DeviceTemplateDetails onClose={()=>{}} onComplete={()=>{}}/>} />
            </Routes>
        </DeviceTemplateManagementWrapper>
    );
}

export default DeviceTemplateManagement;
